import { useAppThunkDispatch, useTypedSelector } from '@Features/store';
import {
	Button,
	ButtonGroup,
	Checkbox,
	CircularProgress,
	FormControlLabel,
	Grow,
	LinearProgress,
	MenuItem,
	MenuList,
	Modal,
	Paper,
	Popper,
} from '@mui/material';
import { FlexLayout, GridLayout, Spacer } from '@Styled/utilities';
import React, { FC, useEffect, useState } from 'react';
import styled from 'styled-components';
import { CloneRequester } from './Services/Clone.requester';
import { useSnackbar } from '@Providers/useSnackbar';
import { unitsGetAll } from '../Contents/Slices/Units.slice';
import { DropButton } from '../Web Content/Pages/AddWebContent/AddWebContent';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import {
	fetchCoursesDropdown,
	fetchUnitsDropdown,
	resetClassroomDropdowns,
} from '@Features/dropdowns/dropdownsSlice';
import { Controller, useForm } from 'react-hook-form';
import { object } from 'yup';
import { number } from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import {
	DropdownCourse,
	DropdownUnit,
} from '@Features/dropdowns/dropdownsSlice.types';
import { DevTool } from '@hookform/devtools';
import { Autocomplete } from '@Components/Autocomplete/Autocomplete';
import api from '@Services/api';
import { useIsStandaloneLecture } from '@ContentManagement/ContentModeContext';

const SUPPORTED_CLONE_TYPES = ['document', 'video', 'test', 'webcontent', 'webinar'];

const schema = object({
	course: number(),
	classroom: number(),
	unit: number(),
});
type Props = {
	open: boolean;
	handleModelClose: React.Dispatch<boolean>;
};

type Form = {
	classroom: number | null;
	course: number | null;
	unit: number | null;
};
const CloneModal: FC<Props> = ({ open, handleModelClose }) => {
	const { displaySnackbar } = useSnackbar();
	const isStandaloneLecture = useIsStandaloneLecture();
	const dispatch = useAppThunkDispatch();
	const anchorRef = React.useRef<HTMLDivElement>(null);
	const [openButton, setOpenButton] = useState(false);
	const [allCourses, setAllCourses] = useState<
		Array<DropdownCourse> | undefined
	>([]);
	const [allUnits, setAllUnits] = useState<Array<DropdownUnit> | undefined>([]);
	const [isCloning, setIsCloning] = useState(false);
	const [standaloneCourses, setStandaloneCourses] = useState<any[]>([]);
	const [cloneAll, setCloneAll] = useState(false);
	const [unitResetKey, setUnitResetKey] = useState(0);
	const [courseResetKey, setCourseResetKey] = useState(0);

	const {
		control,
		reset,
		watch,
		setValue,
	} = useForm<Form>({
		resolver: yupResolver(schema),
		defaultValues: {
			course: null,
			classroom: null,
			unit: null,
		},
	});

	const classroom = watch('classroom');
	const course = watch('course');
	const unit = watch('unit');

	const classrooms = useTypedSelector(
		(state) => state.dropdowns.dropdownClassrooms
	);

	const { currentCourse } = useTypedSelector((state) => state.classroom);

	useEffect(() => {
		if (!open || !isStandaloneLecture) return;
		api.post('standalone-courses/all', {
			page: 1,
			perPage: 100,
			filters: [],
			query: '',
			sortBy: { field: 'order', direction: 'asc' },
		})
			.then((response) => setStandaloneCourses(response.data.data || []))
			.catch((e) =>
				displaySnackbar(
					'error',
					e.response?.data?.message || 'Unable to load standalone courses'
				)
			);
	}, [isStandaloneLecture, open]);

	const handleToggle = () => {
		setOpenButton((prevOpen) => !prevOpen);
	};

	const handleClose = (event: Event) => {
		if (anchorRef.current?.contains(event.target as HTMLElement)) {
			return;
		}
		setOpenButton(false);
	};

	const handleCloneRequest = async (fullReset: boolean = true) => {
		if (isCloning) return;
		setIsCloning(true);
		try {
			if (!currentCourse?.id) return;

			if (cloneAll) {
				// Bulk clone — filter out unsupported types (e.g. assignment)
				const cloneable = allUnits?.filter((u) =>
					SUPPORTED_CLONE_TYPES.includes(u.unitType.name)
				) ?? [];
				const skipped = (allUnits?.length ?? 0) - cloneable.length;

				const unitIds = cloneable.map((u) => u.id);
				await CloneRequester.getInstance().cloneAll(currentCourse.id, unitIds);
				displaySnackbar('success', `Cloned ${unitIds.length} units successfully`);

				if (skipped > 0) {
					displaySnackbar('success', `${skipped} assignment unit(s) were not cloned (unsupported type)`);
				}
			} else if (unit) {
				// Single unit clone
				const {
					data: { message },
				} = await CloneRequester.getInstance().clone(currentCourse.id, unit);
				displaySnackbar('success', message);
			}

			dispatch(unitsGetAll(currentCourse.id as number));
			if (fullReset) dispatch(resetClassroomDropdowns());
			reset({
				classroom: fullReset ? null : classroom,
				course: fullReset ? null : course,
				unit: null,
			});
			setUnitResetKey((prev) => prev + 1);
			if (fullReset) setCloneAll(false);
		} catch (e) {
			const msg = e.response?.data.message || 'Unable To Clone Unit';
			displaySnackbar('error', msg);
		} finally {
			setIsCloning(false);
		}
	};
	const onResetClone = async () => {
		await handleCloneRequest(false);
		setOpenButton(false);
	};

	const onClone = async () => {
		await handleCloneRequest();
		handleModelClose(false);
	};

	const onClassroomChange = async (
		event: React.ChangeEvent<HTMLInputElement>
	) => {
		setValue('course', null);
		setValue('unit', null);
		setCloneAll(false);
		setCourseResetKey((prev) => prev + 1);
		setUnitResetKey((prev) => prev + 1);
		const value = Number(event.target.value);
		if (!value) {
			setValue('classroom', null);
			resetOptions(false);
			return;
		}
		setValue('classroom', value);
		try {
			if (isStandaloneLecture) {
				const response = await api.post('standalone-courses/lectures/all', {
					standalone_course_id: value,
					page: 1,
					perPage: 100,
					filters: [],
					query: '',
					sortBy: { field: 'order', direction: 'asc' },
				});
				setAllCourses((response.data.data || []).filter((lecture: any) => lecture.id !== currentCourse?.id));
				return;
			}
			const courses = await dispatch(fetchCoursesDropdown(value)).unwrap();
			setAllCourses(courses);
		} catch (e) {
			setAllCourses([]);
			displaySnackbar(
				'error',
				e.response?.data?.message ||
					(isStandaloneLecture
						? 'Unable to load lectures'
						: 'Unable to load courses')
			);
		}
	};

	const onCourseChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
		const value = Number(event.target.value);
		if (!value) return;
		setValue('course', value);
		setValue('unit', null);
		setCloneAll(false);
		setUnitResetKey((prev) => prev + 1);
		try {
			if (isStandaloneLecture) {
				const response = await api.post('units/all', { course_id: value, standalone_allow_source: true });
				setAllUnits(response.data.data || []);
			} else {
				const units = await dispatch(fetchUnitsDropdown(value)).unwrap();
				setAllUnits(units);
			}
		} catch (e) {
			setAllUnits([]);
			displaySnackbar(
				'error',
				e.response?.data?.message || 'Unable to load units'
			);
		}
	};
	const resetOptions = (withClose = true) => {
		reset();
		setAllCourses(undefined);
		setAllUnits(undefined);
		setCloneAll(false);
		setCourseResetKey((prev) => prev + 1);
		setUnitResetKey((prev) => prev + 1);
		if (withClose) handleModelClose(false);
	};

	const isCloneDisabled = !course || !classroom || (!unit && !cloneAll) || isCloning;

	return (
		<Modal
			open={open}
			onClose={() => {
				resetOptions(true);
			}}
			aria-labelledby="modal-modal-title"
			aria-describedby="modal-modal-description"
		>
			<ModelContainer>

				<FlexLayout
					flexDirection="column"
					gridGap="0.75rem"
					padding="2.313rem 1.375rem"
					minWidth={{ lg: '60vw' }}
				>
					<Title>{isStandaloneLecture ? 'Clone unit from another standalone Lecture' : 'Clone unit from another course'}</Title>
					{isCloning && (
						<LinearProgress sx={{ width: '100%', borderRadius: 2 }} />
					)}
					<Text>
						Please select the desired unit to be cloned to this course:
					</Text>
					<InputsWrapper gridGap={'1.5rem'}>
						<FlexLayout
							flexDirection="column"
							gridGap="0.4rem"
							mt="1rem"
						>
							<Controller
								control={control}
								name="classroom"
								render={({ field: { onChange, ...others } }) => (
									<Autocomplete
									options={(isStandaloneLecture ? standaloneCourses : classrooms).map((cr: any) => ({
											id: cr.id,
											label: cr.label ? `${cr.title} (${cr.label})` : cr.title,
										}))}
										{...others}
										onChange={onClassroomChange as any}
									label={isStandaloneLecture ? 'Select Standalone Course' : 'Select Classroom'}
									/>
								)}
							/>
						</FlexLayout>
						<FlexLayout
							flexDirection="column"
							gridGap="0.4rem"
							mt="1rem"
						>
							<Controller
								control={control}
								name="course"
								render={({ field, fieldState: { isTouched } }) => (
									<Autocomplete
										key={courseResetKey}
										options={(allCourses || [])?.map((c) => ({
											...c,
											id: c.id,
											label: isStandaloneLecture ? `${c.name} — ${(c as any).section?.name || ''}` : c.name,
										}))}
										{...field}
									label={isStandaloneLecture ? 'Select Lecture' : 'Select Course'}
										onChange={onCourseChange as any}
										textFieldProps={{
											error: Boolean(
												classroom && allCourses && allCourses.length <= 0
											),
											helperText:
												(classroom &&
													allCourses &&
													allCourses.length <= 0 &&
													(isStandaloneLecture
														? 'No other lectures in this standalone course'
														: 'No courses found')) ||
												undefined,
										}}
									/>
								)}
							/>
						</FlexLayout>
						<FlexLayout
							flexDirection="column"
							gridGap="0.4rem"
							mt="1rem"
						>
							<Controller
								control={control}
								name="unit"
								render={({ field }) => (
									<>
										<Autocomplete
											key={unitResetKey}
											disabled={cloneAll}
											options={allUnits
												?.filter((unit) =>
													SUPPORTED_CLONE_TYPES.includes(
														unit.unitType?.name as string
													)
												)
												.map((unit) => ({
													id: unit.id,
													label: unit.name,
												}))}
											label="Select Unit"
											{...field}
											textFieldProps={{
												error: Boolean(
													course && allUnits && allUnits.length <= 0
												),
												helperText:
													(course &&
														allUnits &&
														allUnits.length <= 0 &&
														'No Units found') ||
													undefined,
											}}
										/>
									</>
								)}
							/>
							{allUnits && allUnits.length > 0 && (
								<FormControlLabel
									control={
										<Checkbox
											checked={cloneAll}
											onChange={(e) => {
												setCloneAll(e.target.checked);
												if (e.target.checked) {
													setValue('unit', null);
													setUnitResetKey((prev) => prev + 1);
												}
											}}
											size="small"
											sx={{
												color: '#6c63ff',
												'&.Mui-checked': { color: '#6c63ff' },
											}}
										/>
									}
									label="Clone all units"
									sx={{
										alignSelf: 'flex-start',
										mt: '-0.25rem',
										'& .MuiFormControlLabel-label': {
											fontSize: '0.8rem',
											fontWeight: 500,
										},
									}}
								/>
							)}
						</FlexLayout>
					</InputsWrapper>
				</FlexLayout>
				<FlexLayout justifyContent="flex-end" marginBottom="3rem">
					<Button
						variant="text"
						color="warning"
						onClick={() => {
							resetOptions();
						}}
					>
						Cancel
					</Button>
					<Spacer mx="0.5rem" />
					<Group variant="contained" ref={anchorRef} aria-label="split button">
						<CloneButton
							onClick={() => {
								onClone();
							}}
							variant="outlined"
							disabled={isCloneDisabled}
						>
							{isCloning ? <CircularProgress size={20} /> : 'Clone'}
						</CloneButton>
						<Drop
							size="small"
							aria-controls={openButton ? 'split-button-menu' : undefined}
							aria-expanded={openButton ? 'true' : undefined}
							onClick={handleToggle}
							variant="outlined"
							disabled={isCloneDisabled}
						>
							<ArrowDropDownIcon />
						</Drop>
					</Group>
					<Popper
						sx={{
							zIndex: 1,
						}}
						open={openButton}
						anchorEl={anchorRef.current}
						role={undefined}
						transition
						disablePortal
					>
						{({ TransitionProps, placement }) => (
							<Grow
								{...TransitionProps}
								style={{
									transformOrigin:
										placement === 'bottom' ? 'center top' : 'left bottom',
								}}
							>
								<Paper>
									<ClickAwayListener onClickAway={handleClose}>
										<MenuList id="split-button-menu" autoFocusItem>
											<MenuItemStyle
												key={1}
												onClick={() => {
													onResetClone();
												}}
												disabled={isCloneDisabled}
											>
												Clone and add another
											</MenuItemStyle>
										</MenuList>
									</ClickAwayListener>
								</Paper>
							</Grow>
						)}
					</Popper>
				</FlexLayout>
			</ModelContainer>
		</Modal>
	);
};

const ModelContainer = styled.div`
	height: 23rem;
	position: absolute;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
	border-radius: 4px;
	border: 0;
	background-color: #ffffff;
	outline: none;
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	padding: 0 2rem 0 2rem;
`;
const Title = styled.p`
	font-size: 1.3rem;
	font-weight: 600;
	color: rgba(0, 0, 0, 0.6);
`;
const Text = styled.p`
	font-size: 0.8rem;
	font-weight: normal;
	color: #251b33;
	padding: 0 2.625rem;
	margin-bottom: 1.5rem;
`;

const MenuItemStyle = styled(MenuItem)`
	min-width: 10.313rem;
	font-size: 0.875rem;
	padding: 0 0.75rem;
`;
const CloneButton = styled(Button)`
	color: #6c63ff !important;
	border-color: #6c63ff !important;
	font-size: 0.875rem;
	font-weight: 500;
	&:hover {
		border-color: #6c63ff !important;
	}
	&:disabled {
		color: rgba(0, 0, 0, 0.26) !important;
		border: 1px solid rgba(0, 0, 0, 0.12) !important;
	}
`;
const Drop = styled(DropButton)`
	color: #6c63ff;
	border-color: #6c63ff;
	&:hover {
		border-color: #6c63ff;
	}
`;
const Group = styled(ButtonGroup)`
	box-shadow: none;
	border-radius: unset;
	height: 2.25rem;
	.MuiButtonGroup-grouped {
		min-width: 40px !important;
	}
`;
const Name = styled.span`
	white-space: nowrap;
	text-overflow: ellipsis;
	overflow: hidden;
	width: 100px;
`;

const InputsWrapper = styled(GridLayout)`
	grid-template-columns: repeat(3, 1fr);
`;
export default CloneModal;
